import {
    Cell,
    CellNode,
    Column,
    RowNode,
    Sort,
    SortOrder,
} from '../../coaching-observation/experimental.grid';
import {
    ModelSetTransformer,
    ModelTransformer,
} from '../../common/common.model';
import {
    OverallCoverageItem,
    PeriodCoverageItem,
    RosterType,
} from '../coverage.coaching-insight.model';
import { ScheduleEventDto } from '../schedule.coaching-insight.model';
import { EventType } from '../schedule/models/schedule-event.model';

import { DateRange } from './select-period-date-range.component';

export enum ColumnType {
    NAME,
    OA_SCORE,
    OVERALL_COACHING_PERCENT,
    PERIOD,
    COACHING_COVERAGE_PERCENT,
    OBSERVATION_COVERAGE_PERCENT,
    COMMITMENT_COVERAGE_PERCENT,
}

export enum TemplateID {
    HEADER = 'header',
    BOTH = 'both',
    SELF = 'self',
    TEAM = 'team',
}

export interface ColumnContext {
    type: ColumnType;
    templateId: TemplateID;
    week?: DateRange;
    isExpanded?: boolean;
}

export interface RowContext {
    isExpanded?: boolean;
    depth?: number;
}

export interface CellContext {
    templateId: TemplateID;
    user: RosterItem;
    overall: OverallCoverageItem;
    period?: PeriodCoverageItem;
    schedule?: ScheduleEventDto[];
}

export interface PeriodClickEvent {
    column: Column<ColumnContext>;
    elementSelector: string;
}

export interface OtherScheduleItemsClickEvent {
    cell: Cell<CellContext>;
    type: EventType;
    elementSelector: string;
}

export interface ExtRosterItem {
    userId: number;
    firstName: string;
    lastName: string;
    rosterTypeId: RosterType;
    supergroupId: number;
    supergroupName: string;
    roleId: number;
    roleName: string;
    oaScore: number;
    populationGroup: string;
    oaThreshold: string;
    managerId: number;
}

export interface RosterItem extends ExtRosterItem {
    fullName: string;
}

export class RosterTransformer extends ModelSetTransformer<
    ExtRosterItem,
    RosterItem
> {
    protected newTransformer(): ModelTransformer<ExtRosterItem, RosterItem> {
        throw new Error('Method not implemented.');
    }

    transform(model: ExtRosterItem[]): RosterItem[] {
        model.sort((a, b) => {
            return b.oaScore - a.oaScore;
        });

        for (let i = 0; i < model.length; i++) {
            if (model[i].populationGroup === null) {
                const a = model.splice(i, 1); // removes the item
                model.unshift(a[0]); // adds it back to the beginning
                break;
            }
        }

        return model.map((m: ExtRosterItem) => {
            return {
                ...m,
                fullName: `${m.firstName} ${m.lastName}`,
            };
        });
    }
}

export function compare(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    try {
        const columnType = parseInt(def.columnId.split('-')[0]);
        const mode: 'number' | 'string' =
            columnType === ColumnType.NAME ? 'string' : 'number';
        const ctxA: CellContext = cellA.context.ctx;
        const ctxB: CellContext = cellB.context.ctx;
        let valA: any;
        let valB: any;
        switch (columnType) {
            case ColumnType.NAME:
                valA = ctxA.user.fullName;
                valB = ctxB.user.fullName;
                break;
            case ColumnType.OA_SCORE:
                valA = ctxA.user.oaScore;
                valB = ctxB.user.oaScore;
                break;
            case ColumnType.OVERALL_COACHING_PERCENT:
                valA = ctxA.overall.overallCoveragePct;
                valB = ctxB.overall.overallCoveragePct;
                break;
            case ColumnType.COACHING_COVERAGE_PERCENT:
                valA = ctxA.period.coverageScore;
                valB = ctxB.period.coverageScore;
                break;
            case ColumnType.OBSERVATION_COVERAGE_PERCENT:
                valA = ctxA.period.observationCoverageScore;
                valB = ctxB.period.observationCoverageScore;
                break;
            case ColumnType.COMMITMENT_COVERAGE_PERCENT:
                valA = ctxA.period.commitmentCoverageScore;
                valB = ctxB.period.commitmentCoverageScore;
                break;
            default:
                console.error('invalid column');
                return Number.MIN_SAFE_INTEGER;
        }
        if (mode === 'number') {
            valA = valA == null ? Number.MIN_SAFE_INTEGER : valA;
            valB = valB == null ? Number.MIN_SAFE_INTEGER : valB;
        } else {
            valA = valA == null ? '' : valA;
            valB = valB == null ? '' : valB;
        }
        return mode === 'number'
            ? def.order === SortOrder.ASC
                ? valA - valB
                : valB - valA
            : def.order === SortOrder.ASC
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
    } catch (e) {
        console.log(e);
        return Number.MIN_SAFE_INTEGER;
    }
}
